import { AbstractControl, ValidationErrors, ValidatorFn } from '@angular/forms';
import { NgbDate, NgbDateStruct } from '@ng-bootstrap/ng-bootstrap';
import {
  NO_WHITESPACE_AND_SYMBOLS,
  LOCAL_DATE_PATTERN,
} from '../zn-other-actions/constants/validations-constants';

export function positiveIntegerValidator(control: AbstractControl) {
  const val = Number(control.value);
  return control.value && Number.isInteger(val) && val > 0
    ? null
    : { positiveInteger: true };
}

export function noFutureDateValidator(): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    return isFutureDateValue(control.value) ? { futureDate: true } : null;
  };
}

export function futureDateDisabled(date: NgbDate | NgbDateStruct): boolean {
  return isFutureDateValue(date);
}

export function handleDateBlur(
  inputElement: HTMLInputElement,
  control: AbstractControl | null,
): void {
  const rawValue = inputElement.value?.trim();

  if (!control) {
    return;
  }

  control.markAsTouched();

  if (!rawValue) {
    control.setValue(null);
    return;
  }

  const match = LOCAL_DATE_PATTERN.exec(rawValue);
  if (!match) {
    control.setErrors({ ...control.errors, invalidDate: true });
    return;
  }

  const [, dayText, monthText, yearText] = match;
  const day = Number.parseInt(dayText, 10);
  const month = Number.parseInt(monthText, 10);
  const year = Number.parseInt(yearText, 10);

  const parsed = new Date(year, month - 1, day);
  const isRealDate =
    parsed.getFullYear() === year &&
    parsed.getMonth() === month - 1 &&
    parsed.getDate() === day;

  if (!isRealDate) {
    control.setErrors({ ...control.errors, invalidDate: true });
    return;
  }

  const parsedDateStruct: NgbDateStruct = { year, month, day };
  inputElement.value = formatLocalDate(day, month, year);

  if (isFutureDateValue(parsedDateStruct)) {
    control.setValue(parsedDateStruct, { emitEvent: false });
    control.updateValueAndValidity();
    return;
  }

  control.setValue(parsedDateStruct);
  if (control.hasError('futureDate') || control.hasError('invalidDate')) {
    control.updateValueAndValidity();
  }
}

function isFutureDateValue(value: unknown): boolean {
  const selected = parseSupportedDateValue(value);

  if (!selected) {
    return false;
  }

  const today = new Date();
  today.setHours(0, 0, 0, 0);
  selected.setHours(0, 0, 0, 0);

  return selected > today;
}

function parseSupportedDateValue(value: unknown): Date | null {
  if (!value) {
    return null;
  }

  if (value instanceof Date) {
    return Number.isNaN(value.valueOf()) ? null : new Date(value);
  }

  if (isNgbDateStruct(value)) {
    const parsed = new Date(value.year, value.month - 1, value.day);
    return Number.isNaN(parsed.valueOf()) ? null : parsed;
  }

  if (typeof value !== 'string') {
    return null;
  }

  const trimmedValue = value.trim();
  if (!trimmedValue) {
    return null;
  }

  const localFormatMatch = LOCAL_DATE_PATTERN.exec(trimmedValue);
  if (localFormatMatch) {
    const [, day, month, year] = localFormatMatch;
    const parsed = new Date(Number(year), Number(month) - 1, Number(day));
    return Number.isNaN(parsed.valueOf()) ? null : parsed;
  }

  const parsed = new Date(trimmedValue);
  return Number.isNaN(parsed.valueOf()) ? null : parsed;
}

function isNgbDateStruct(value: unknown): value is NgbDateStruct {
  return (
    typeof value === 'object' &&
    value !== null &&
    'year' in value &&
    'month' in value &&
    'day' in value
  );
}

export function noWhitespaceValidator(control: AbstractControl) {
  const value = control.value as string;
  if (!value) return null;
  return NO_WHITESPACE_AND_SYMBOLS.test(value) ? null : { whitespace: true };
}

export function maxDecimalsValidator(decimals: number = 2) {
  return (control: AbstractControl): ValidationErrors | null => {
    if (control.value == null || control.value === '') return null;

    const stringValue = String(control.value).replace(',', '.');
    const parts = stringValue.split('.');
    const decimalPart = parts[1] ?? '';

    return decimalPart.length > decimals
      ? { maxDecimals: { max: decimals, actual: decimalPart.length, maxDecimals: true } }
      : null;
  };
}

export function preventScientificNotation(event: KeyboardEvent): void {
  if (event.key === 'e' || event.key === 'E') {
    event.preventDefault();
  }
}

function formatLocalDate(day: number, month: number, year: number): string {
  return `${String(day).padStart(2, '0')}/${String(month).padStart(2, '0')}/${year}`;
}
